import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Box, CircularProgress, IconButton, Tooltip} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import CropFreeIcon from '@mui/icons-material/CropFree';
import CropOriginalOutlinedIcon from '@mui/icons-material/CropOriginalOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {useTranslation} from 'react-i18next';

export type CapturePick = 'select-area' | 'full-image';

interface Props {
    onPick: (pick: CapturePick) => void;
    onCancel: () => void;
    capturing?: CapturePick;
}

const DEFAULT_TOP = 8;

/**
 * Floating capture toolbar. Two pure-trigger buttons — clicking either
 * starts that capture mode. While `capturing` is set, the matching button
 * shows a spinner (via LoadingButton's loadingPosition="start" so no
 * layout shift) and every other control is disabled.
 */
const AssistantScreenshotToolbar: React.FC<Props> = ({onPick, onCancel, capturing}) => {
    const {t} = useTranslation();
    const barRef = useRef<HTMLDivElement | null>(null);

    // null while we're still centered; once the user drags we switch to
    // absolute pixel coords
    const [pos, setPos] = useState<{x: number; y: number} | null>(null);
    const dragRef = useRef<{startX: number; startY: number; origX: number; origY: number} | null>(null);
    const [dragging, setDragging] = useState(false);

    const busy = !!capturing;

    // LoadingButton's default spinner inherits the (disabled) text color, which
    // is a faint grey on the white toolbar — almost invisible during capture.
    // Render an explicit brand-primary, thicker indicator so the in-progress
    // state reads clearly.
    const spinner = (
        <CircularProgress size={18} thickness={5} sx={{color: 'primary.main'}}/>
    );

    const onHandlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        const el = barRef.current;
        if (!el || busy) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const rect = el.getBoundingClientRect();
        dragRef.current = {startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top};
        setDragging(true);
    };

    const onHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const d = dragRef.current;
        const el = barRef.current;
        if (!d || !el) return;
        const rect = el.getBoundingClientRect();
        const maxX = Math.max(0, window.innerWidth - rect.width);
        const maxY = Math.max(0, window.innerHeight - rect.height);
        setPos({
            x: Math.min(Math.max(0, d.origX + (e.clientX - d.startX)), maxX),
            y: Math.min(Math.max(0, d.origY + (e.clientY - d.startY)), maxY),
        });
    };

    const onHandlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        dragRef.current = null;
        setDragging(false);
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
            // ignore — capture may already be released
        }
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !busy) {
                e.preventDefault();
                onCancel();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel, busy]);

    return createPortal(
        <Box
            ref={barRef}
            data-screenshot-overlay
            // stay visible during capture so the progress spinner shows; snapdom
            // still drops us from the shot via its "remove" exclude
            data-screenshot-keep-visible
            sx={theme => ({
                position: 'fixed',
                ...(pos
                    ? {top: pos.y, left: pos.x}
                    : {top: DEFAULT_TOP, left: '50%', transform: 'translateX(-50%)'}),
                zIndex: 1400,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.5),
                borderRadius: 1,
                px: 0.5,
                py: 0.5,
                userSelect: 'none',
                // glow so the floating picker is hard to miss; gently pulses
                // unless the user is dragging it
                boxShadow: `0 6px 20px rgba(0,0,0,0.14), 0 0 0 3px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 18px 2px ${alpha(theme.palette.primary.main, 0.5)}`,
                animation: dragging ? 'none' : 'captureToolbarGlow 1.8s ease-in-out infinite',
                '@keyframes captureToolbarGlow': {
                    '0%, 100%': {
                        boxShadow: `0 6px 20px rgba(0,0,0,0.14), 0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}, 0 0 12px 1px ${alpha(theme.palette.primary.main, 0.35)}`,
                    },
                    '50%': {
                        boxShadow: `0 6px 20px rgba(0,0,0,0.14), 0 0 0 3px ${alpha(theme.palette.primary.main, 0.6)}, 0 0 28px 5px ${alpha(theme.palette.primary.main, 0.6)}`,
                    },
                },
            })}
        >
            <Box
                onPointerDown={onHandlePointerDown}
                onPointerMove={onHandlePointerMove}
                onPointerUp={onHandlePointerUp}
                onPointerCancel={onHandlePointerUp}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: busy ? 'not-allowed' : dragging ? 'grabbing' : 'grab',
                    color: 'text.disabled',
                    opacity: busy ? 0.5 : 1,
                    touchAction: 'none',
                }}
            >
                <DragIndicatorIcon fontSize="small"/>
            </Box>

            <Box sx={{width: '1px', height: 20, bgcolor: 'divider', mx: 0.5}}/>

            <LoadingButton
                size="small"
                variant="text"
                color="inherit"
                loading={capturing === 'select-area'}
                loadingPosition="start"
                loadingIndicator={spinner}
                disabled={busy && capturing !== 'select-area'}
                startIcon={<CropFreeIcon/>}
                onClick={() => onPick('select-area')}
            >
                {t('Select area')}
            </LoadingButton>

            <LoadingButton
                size="small"
                variant="text"
                color="inherit"
                loading={capturing === 'full-image'}
                loadingPosition="start"
                loadingIndicator={spinner}
                disabled={busy && capturing !== 'full-image'}
                startIcon={<CropOriginalOutlinedIcon/>}
                onClick={() => onPick('full-image')}
            >
                {t('Full image')}
            </LoadingButton>

            <Box sx={{width: '1px', height: 20, bgcolor: 'divider', mx: 0.5}}/>

            <Tooltip title={t('Cancel · Esc')}>
                <span>
                    <IconButton
                        size="small"
                        disabled={busy}
                        onClick={onCancel}
                        aria-label={t('Cancel screenshot')}
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </span>
            </Tooltip>
        </Box>,
        document.body,
    );
};

export default AssistantScreenshotToolbar;
